<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：サイドバー
 *
 * 作成者：2021/11/23 ノア）虞
 * 最終修正者：2022/01/14 ノア）虞
 *
 -->
<template>
  <nav class="tti-hamburger-list" :class="{ ttiHamburgerListOpen: isShow }">
    <div class="tti-hamburger-list-contents">
      <!-- メニュー -->
      <div class="tti-hamburger-list-menus">
        <!-- お気に入り -->
        <div class="menuList border">
          <div class="menu-items" @click="showFavorite = !showFavorite">
            <span class="expander">
              <span v-if="showFavorite"><tti-svg-icon icon-name="su_021"></tti-svg-icon></span>
              <span v-else><tti-svg-icon icon-name="su_022"></tti-svg-icon></span>
            </span>
            <tti-svg-icon icon-name="wo_001"></tti-svg-icon>
            <span class="menu-text">{{ $t('favorite.title') }}</span>
          </div>
          <div
            class="menu-items-child"
            v-show="showFavorite"
            v-for="menuId in favoriteMenuList"
            :key="menuId"
            @click.stop="onFavMenuClick(menuId)"
            v-tooltip="{
              content: `${getDefinitionName(menuMaster, menuId, { code: 'code', value: 'tip' })}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            {{ getDefinitionName(menuMaster, menuId) }}
          </div>
        </div>
        <div
          v-for="(el, ind) in menuList"
          :key="ind"
          class="menuList"
          :class="{ border: el.menuId === migrationResult }"
        >
          <div
            class="menu-items"
            v-if="!el.role || (el.role && userRole === el.role) || userRole === UserRole.SYSTEM"
            @click.stop="onMenuClick(el)"
          >
            <span class="expander">
              <div v-if="el.children && el.children.length > 0">
                <span v-if="showMenu(el.menuId)"><tti-svg-icon icon-name="su_021"></tti-svg-icon></span>
                <span v-else><tti-svg-icon icon-name="su_022"></tti-svg-icon></span>
              </div>
            </span>
            <tti-svg-icon :icon-name="el.icon"></tti-svg-icon>
            <span class="menu-text">{{ el.menuName }}</span>
          </div>
          <!-- サブメニュー -->
          <div v-for="(elChild, indChild) in el.children" :key="indChild">
            <div
              class="menu-items-child"
              v-show="showMenu(el.menuId)"
              v-if="userRole === UserRole.SYSTEM || userRole === elChild.role"
              @click.stop="onMenuClick(elChild)"
              v-tooltip="{
                content: `${getDefinitionName(menuMaster, elChild.menuId, { code: 'code', value: 'tip' })}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
            >
              {{ elChild.menuName }}
            </div>
          </div>
        </div>
        <!-- 機能一覧 -->
        <div class="menuList">
          <div class="menu-items" @click="onMenuListClick()">
            <span class="expander"></span>
            <tti-svg-icon icon-name="wo_011"></tti-svg-icon>
            <div class="menu-text">{{ $t('menu.screens') }}</div>
          </div>
        </div>
      </div>

      <!-- ログアウトボタンとバージョン情報 -->
      <div class="tti-hamburger-list-bottom">
        <div class="tti-logout">
          <el-button
            class="tti-btn-default tti-btn-001 menuLogout"
            @click="onLogout"
            v-tooltip="{
              content: `${$t('headerMenu.loginoutTip')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="su_001"></tti-svg-icon>
            {{ $t('headerMenu.loginout') }}
          </el-button>
        </div>
        <div class="tti-flex-row">
          <copyright></copyright>
          <span class="nav-bar-version">V{{ systermVersion }}</span>
        </div>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator'
import { cloneDeep } from 'lodash'

import Copyright from '@/components/Copyright.vue'
import { dialog, common } from '@/store/modules'
import config from '@/config/config'
import { getMenuConfig, getMenuMasterConfig } from '@/config'
import { MenuChild, MenuItem } from '@/models/'
import { PageType, MainOfficeCode, ScreenListMenuId, UserRole, MenuIds } from '@/commons/constants'
import { localService } from '@/commons/services/localService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { i18n } from '@/locales'
import HelperService from '@/service/helperService'
import { postLogout } from '@/store/apis'
import { clearLocal, clearSession } from '@/commons/services/localService'
import { moveService } from '@/commons/services/moveService'
import packageConfig from '../../package.json'
import { clearWindowCookie } from '@/service/refreshAndCloseWindowService'

@Component({
  name: 'TtiSilderBar',
  components: { Copyright }
})
export default class TtiSilderBar extends Vue {
  /**
   * ユーザー情報
   *
   * @returns {string} ユーザー情報
   */
  get userInfo(): string {
    if (!localService.userInfo?.userId) {
      return `${localService.userInfo?.userName || ''}`
    }

    return `${localService.userInfo?.userName || ''}(${localService.userInfo?.userId})`
  }

  /**
   * 企業情報
   *
   * @returns {string} 企業情報
   */
  get companyInfo(): string {
    if (!localService.userInfo?.companyCode) {
      return `${localService.userInfo?.companyName || ''}`
    }

    return `${localService.userInfo?.companyName || ''}(${localService.userInfo?.companyCode})`
  }

  /**
   * 気に入りメニュー
   */
  favoriteMenuList: string[] = []

  /**
   * 気に入りメニュー
   *
   * @returns {string[]} 気に入りメニュー
   */
  get watchedFavMenuList(): string[] {
    return common.favMenuList
  }

  /**
   * 営業所情報
   *
   */
  mainOffice = `${i18n.t('headerMenu.mainOffice')}(${MainOfficeCode})`

  /**
   * ダイアログボックスを表示するかどうかを取得する（画面用）
   *
   * @returns {boolean} ダイアログボックスを表示するかどうか（画面用）
   */
  get isShow(): boolean | undefined {
    this.initMenuList()
    return dialog.silderBarIsShow
  }

  /**
   * ダイアログボックスを表示するかどうかを設定する（画面用）
   */
  set isShow(value: boolean | undefined) {
    this.handelClose()
  }
  /**
   * 機能一覧汎用定義
   */
  menuMaster = getMenuMasterConfig().menuMaster

  /**
   * メニュー情報
   */
  menuList = getMenuConfig().menuList

  /**
   * ユーザ種類
   */
  userRole = localService?.userInfo?.role || ''

  /**
   * ユーザ種類
   *
   * @readonly
   * @constant
   */
  UserRole = UserRole

  /**
   * 気に入りメニュー変更した時の処理
   *
   * @param {string[]} value  気に入りメニュー
   * @returns {void}
   */
  @Watch('watchedFavMenuList', { immediate: true, deep: true })
  onDataChange(value: string[]): void {
    this.favoriteMenuList = cloneDeep(value)
  }

  getDefinitionName = HelperService.getDefinitionName

  /**
   * システムバージョン
   */
  systermVersion = packageConfig.version

  /**
   * 操作ポップアップボックスが表示されている、または表示されていない
   */
  handelClose(): void {
    dialog.setSilderBarIsShow()
  }

  /**
   * メニュー情報初期化
   */
  initMenuList(): void {
    this.menuList = getMenuConfig().menuList
    this.menuMaster = getMenuMasterConfig().menuMaster
    this.judgeMenuShow()
  }

  /**
   * 機能一覧サブ画面の表示フラグを設定する
   *
   */
  judgeMenuShow(): void {
    if (localService.itpConnectValidFlag === '1') {
      this.menuList.forEach((el: MenuItem) => {
        if (el.icon === 'wo_007') {
          el.children.push({
            type: PageType.PAGE,
            menuId: 'connectCompanyMaster',
            menuName: i18n.t('menu.connectCompanyMaster') as string,
            role: UserRole.SYSTEM
          })
        }
      })

      this.menuMaster.push({
        code: 'connectCompanyMaster',
        value: i18n.t('menu.connectCompanyMaster').toString(),
        tip: i18n.t('menu.connectCompanyMasterTip').toString()
      })
    }
  }

  /**
   * ログアウト
   *
   * @returns {void}
   */
  onLogout(): void {
    const confirmCallback = async () => {
      await moveService.beforeLogout()
      this.doLogout()
    }

    msgBoxService.confirm(i18n.t('messages.logoutConfirm').toString(), confirmCallback)
  }

  /**
   * ログアウトを実行する。
   *
   * @returns {void}
   */
  async doLogout(): Promise<void> {
    try {
      await postLogout()
      clearLocal()
      clearSession()
      HelperService.removeAccessToken()
      clearWindowCookie()
      Vue.nextTick(() => {
        this.$router.push(config.menu.login)
      })
    } catch (e) {
      Vue.$log.debug('Login > onLogout: e=', e)
      throw e
    }
  }

  /**
   * メニューをクリックした時の処理
   *
   * @param {MenuItem | MenuChild} element クリックしたメニュー
   * @returns {void}
   */
  onMenuClick(element: MenuItem | MenuChild): void {
    if (element.children && element.children.length > 0) {
      this.changeMenu(element.menuId)
    }

    if ((element.children && element.children.length > 0) || !element.menuId) return

    const menu = element.menuId
    if (element.type === PageType.PAGE) {
      this.$router.push(config.menu[menu])
    }
  }

  /**
   * メお気に入りニューをクリックした時の処理
   *
   * @param {string} menuId クリックしたメニュー
   * @returns {void}
   */
  onFavMenuClick(menuId: string): void {
    if (menuId === ScreenListMenuId) {
      this.onMenuListClick()
    } else {
      this.$router.push(config.menu[menuId])
    }
  }

  /**
   * 機能一覧をクリックした時の処理
   *
   * @returns {void}
   */
  onMenuListClick(): void {
    dialog.setScreenDialogShow(true)
  }

  /**
   * メニューパラメータ
   */
  showFavorite = false
  showMigrationResult = false
  showMaster = false
  migrationResult = MenuIds.MIGRATION_RESULT

  /**
   * サブメニュー表示/非表示する。
   *
   * @param {string | undefined} menuId メニューID
   * @returns {void}
   */
  changeMenu(menuId: string | undefined): void {
    if (menuId === MenuIds.MIGRATION_RESULT) {
      this.showMigrationResult = !this.showMigrationResult
    }
    if (menuId === MenuIds.MASTER) {
      this.showMaster = !this.showMaster
    }
  }

  /**
   * サブメニュー表示/非表示を取得する。
   *
   * @param {string | undefined} menuId メニューID
   * @returns {void}
   */
  showMenu(menuId: string | undefined): boolean {
    if (menuId === MenuIds.MIGRATION_RESULT) {
      return this.showMigrationResult
    }
    if (menuId === MenuIds.MASTER) {
      return this.showMaster
    }
    return false
  }
}
</script>

<style scoped lang="scss">
.tti-hamburger-list {
  position: absolute;
  top: 48px;
  left: -320px;
  z-index: 100;
  width: 320px;
  height: calc(100vh - 48px);
  min-height: 671px;
  text-align: left;
  background: $C02;
  -webkit-transition: 0.3s ease-in-out;
  transition: 0.3s ease-in-out;

  .tti-hamburger-list-profile {
    display: flex;
    padding: 12px;

    .tti-person-bio-column {
      flex: 1;
      margin-left: 12px;

      .tti-person-name {
        width: 216px;
        margin-top: 4px;
        overflow: hidden;
        font-size: $FS01;
        text-overflow: ellipsis;
        word-break: break-all;
      }

      .tti-person-company {
        width: 216px;
        margin-top: 12px;
        overflow: hidden;
        font-size: 12px;
        text-overflow: ellipsis;
        word-break: break-all;
      }

      .tti-person-depart {
        width: 216px;
        margin-top: 8px;
        overflow: hidden;
        font-size: 12px;
        text-overflow: ellipsis;
        word-break: break-all;
      }
    }
  }

  .tti-hamburger-list-menus {
    overflow: auto;
    height: calc(calc(100vh - 48px) - 100px);
    margin-left: 12px;

    .menuList {
      ::v-deep .el-popover__reference {
        width: 100%;
        height: 40px;
        align-items: center;
        display: flex;

        span {
          display: block;
          width: 100%;
        }
      }
    }
  }

  .tti-hamburger-list-bottom {
    position: absolute;
    bottom: 0;
    width: 100%;
    padding-top: 12px;

    .tti-logout {
      text-align: center;
    }

    .nav-bar-version {
      color: rgba(0, 0, 0, 0.45);
      font-size: 14px;
      padding: 0 16px;
      margin: 20px 0 12px 0;
    }
  }
}

.menu-items {
  display: flex;
  // justify-content: space-between;
  height: 40px;
  align-items: center;
  font-size: $FS01;
  color: $C08;

  &:hover {
    background-color: $C42-OP15 !important;
    color: $C08 !important;
    cursor: pointer;
  }

  div {
    display: flex;
    align-items: center;
  }

  .menu-items-i {
    width: 24px;
    margin: 7px 6px 7px 0;
  }
}

.tti-flex-row {
  display: flex;
}

.ttiHamburgerListOpen {
  left: 0;
  box-shadow: 0 8px 32px 1px rgb(0, 0, 0, 90%);
}

.menu-items-child {
  height: 40px;
  padding-left: 95px;
  display: flex;
  align-items: center;
  color: $C08;

  &:hover {
    color: $C08 !important;
  }
}

.last-menu {
  button {
    background: none;
    padding-top: 0;
    padding-bottom: 0;
  }
}

.menu-text {
  margin-left: 6px;
}

.expander {
  margin-right: 6px;
  width: 24px;
}

.menuLogout {
  width: 156px;
}

.border {
  border-bottom: 1px solid $C05;
}
</style>
